import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Progress,
  Space,
  Tooltip,
  Modal,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  message,
  Popconfirm,
  Select,
  Input,
  Radio,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TrophyOutlined,
  AimOutlined,
  CalendarOutlined,
  FilterOutlined,
  TableOutlined,
  AppstoreOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import EnhancedSmartGoalModal from './EnhancedSmartGoalModal';
import EnhancedSmartGoalDetailsModal from './EnhancedSmartGoalDetailsModal';
import MultiGoalCard from './MultiGoalCard';
import PatientGoalTree from './PatientGoalTree';
import { ORGAN_SYSTEM_DISPLAY_NAMES } from '../utils/organSystemConstants';
import {
  getGoalsNeedingReview,
  generateReviewReminders,
  getReviewStatus,
  isReviewOverdue
} from '../utils/reviewSchedulingUtils';
import './SmartGoals.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Utility function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};
const { Search } = Input;

const PatientSmartGoals = ({ patientId, problems = [], refreshTrigger, selectedProblem, onGoalUpdate, onModalCancel }) => {
  const [smartGoals, setSmartGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Delete modal state removed - now handled by Popconfirm in details modal
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'cards', 'tree'
  const [selectedPatientGoalId, setSelectedPatientGoalId] = useState(null);

  // NEW: Enhanced filtering options
  const [systemFilter, setSystemFilter] = useState('all');
  const [problemFilter, setProblemFilter] = useState('all');


  const fetchSmartGoals = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      // NEW: Enhanced filter parameters
      if (systemFilter !== 'all') params.system = systemFilter;
      if (problemFilter !== 'all') params.problem = problemFilter;

      const response = await axios.get(`/patients/${patientId}/smart-goals`, { params });
      setSmartGoals(response.data.data.smartGoals);
    } catch (err) {
      console.error('Error fetching SMART goals:', err);
      setError(err.response?.data?.message || 'Failed to fetch SMART goals');
    } finally {
      setLoading(false);
    }
  }, [patientId, statusFilter, systemFilter, problemFilter]);

  useEffect(() => {
    fetchSmartGoals();
  }, [fetchSmartGoals, refreshTrigger]);

  const handleCreateSuccess = () => {
    fetchSmartGoals();
    setCreateModalVisible(false);
    if (onGoalUpdate) onGoalUpdate();
    // Clear selectedProblem when goal is successfully created
    if (onModalCancel) {
      onModalCancel();
    }
  };

  // Auto-open create modal when selectedProblem is provided
  useEffect(() => {
    if (selectedProblem) {
      setCreateModalVisible(true);
    }
  }, [selectedProblem]);



  const handleDeleteGoal = async (goalId) => {
    try {
      console.log('Attempting to delete SMART goal:', goalId);
      await axios.delete(`/smart-goals/${goalId}`);
      message.success('SMART goal deleted successfully');
      fetchSmartGoals();
      // Trigger parent update if callback provided
      if (onGoalUpdate) {
        onGoalUpdate();
      }
    } catch (error) {
      console.error('Error deleting SMART goal:', error);
      message.error(error.response?.data?.message || 'Failed to delete SMART goal');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'blue',
      'Completed': 'green',
      'Modified': 'orange',
      'Cancelled': 'red',
      'On Hold': 'default',
      // NEW: Enhanced status colors
      'Not Started': 'default',
      'In Progress': 'blue'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Active': <ClockCircleOutlined />,
      'Completed': <CheckCircleOutlined />,
      'Modified': <EditOutlined />,
      'Cancelled': <ExclamationCircleOutlined />,
      'On Hold': <ExclamationCircleOutlined />,
      // NEW: Enhanced status icons
      'Not Started': <ClockCircleOutlined />,
      'In Progress': <ClockCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  // NEW: Helper functions for enhanced fields
  const getPillarColor = (pillar) => {
    const colors = {
      'Medication': 'blue',
      'Nutrition': 'green',
      'Movement': 'orange',
      'Recovery': 'purple',
      'Emotion': 'magenta'
    };
    return colors[pillar] || 'default';
  };

  const getTierColor = (tier) => {
    const colors = {
      1: 'green',
      2: 'blue',
      3: 'orange',
      4: 'red'
    };
    return colors[tier] || 'default';
  };



  const isOverdue = (goal) => {
    if (goal.status === 'Completed' || goal.status === 'Cancelled') {
      return false;
    }
    return moment().isAfter(moment(goal.timeline?.targetDate));
  };

  const getDaysRemaining = (goal) => {
    if (goal.status === 'Completed' || goal.status === 'Cancelled') {
      return 0;
    }
    const target = moment(goal.timeline?.targetDate);
    const now = moment();
    return Math.max(0, target.diff(now, 'days'));
  };

  const filteredGoals = smartGoals.filter(goal => {
    // Search filter
    const matchesSearch = !searchText ||
      (goal.smartGoal && goal.smartGoal.toLowerCase().includes(searchText.toLowerCase())) ||
      (goal.mdSmartGoalOrder && goal.mdSmartGoalOrder.toLowerCase().includes(searchText.toLowerCase())) ||
      (goal.problem?.problemName && goal.problem.problemName.toLowerCase().includes(searchText.toLowerCase()));

    // Status filter
    const matchesStatus = statusFilter === 'all' || goal.status === statusFilter;

    // NEW: System filter
    const matchesSystem = systemFilter === 'all' || goal.system === systemFilter;

    // NEW: Problem filter
    const matchesProblem = problemFilter === 'all' || goal.problem?._id === problemFilter;

    return matchesSearch && matchesStatus && matchesSystem && matchesProblem;
  });

  const columns = [
    {
      title: 'Goal',
      key: 'goal',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text strong>
            {decodeHtmlEntities(record.mdSmartGoalOrder) || 'No clinical objective specified'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Problem: {record.problem?.problemName}
          </Text>
          {record.system && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                System: {ORGAN_SYSTEM_DISPLAY_NAMES[record.system] || record.system}
              </Text>
            </>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => (
        <div>
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {status}
          </Tag>
          {isOverdue(record) && (
            <Tag color="red" style={{ marginTop: 4 }}>
              Overdue
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      render: (text, record) => {
        // Use overallCompletionRate for enhanced SMART goals, fallback to legacy progress
        const percentage = record.overallCompletionRate !== undefined ? record.overallCompletionRate : record.progress?.percentage || 0;

        // Calculate total actions across all patient goals
        let totalActions = 0;
        let completedActions = 0;
        if (record.patientGoals && record.patientGoals.length > 0) {
          record.patientGoals.forEach(pg => {
            if (pg.actions) {
              totalActions += pg.actions.length;
              completedActions += pg.actions.filter(a => a.completed).length;
            }
          });
        } else if (record.actions) {
          // Legacy structure fallback
          totalActions = record.actions.length;
          completedActions = record.actions.filter(a => a.completed).length;
        }

        return (
          <div>
            <Progress
              percent={percentage}
              size="small"
              status={percentage === 100 ? 'success' : 'active'}
            />
            {totalActions > 0 && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {completedActions}/{totalActions} actions
              </Text>
            )}
          </div>
        );
      }
    },
    {
      title: 'Timeline',
      key: 'timeline',
      width: 120,
      render: (text, record) => {
        const targetDate = record.timeline?.targetDate;
        const nextReview = record.timeline?.nextReviewDate;
        const isOverdue = targetDate && moment().isAfter(moment(targetDate));
        const isReviewOverdue = nextReview && moment().isAfter(moment(nextReview));

        return (
          <div>
            {targetDate && (
              <div>
                <Text style={{ fontSize: '12px', color: isOverdue ? '#f5222d' : 'inherit' }}>
                  Target: {moment(targetDate).format('MMM DD')}
                </Text>
              </div>
            )}
            {nextReview && (
              <div>
                <Text style={{ fontSize: '11px', color: isReviewOverdue ? '#fa8c16' : '#999' }}>
                  Review: {moment(nextReview).format('MMM DD')}
                </Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Timeline',
      key: 'timeline',
      width: 150,
      render: (_, record) => {
        const daysRemaining = getDaysRemaining(record);
        const targetDate = moment(record.timeline?.targetDate);
        
        return (
          <div>
            <div style={{ fontSize: '12px' }}>
              <CalendarOutlined /> {targetDate.format('MMM DD, YYYY')}
            </div>
            <Text 
              type={daysRemaining <= 7 ? 'danger' : daysRemaining <= 30 ? 'warning' : 'secondary'}
              style={{ fontSize: '11px' }}
            >
              {daysRemaining > 0 ? `${daysRemaining} days left` : 'Due'}
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Goal">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                console.log('Opening edit modal for goal:', record._id);
                setSelectedGoal(record);
                setIsEditMode(true);
                setDetailsModalVisible(true);
              }}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete Goal">
            <Popconfirm
              title="Delete SMART Goal"
              description={
                <div>
                  <p>Are you sure you want to delete this SMART goal?</p>
                  <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                    This action cannot be undone.
                  </p>
                </div>
              }
              onConfirm={() => {
                console.log('Delete confirmed for goal:', record._id);
                handleDeleteGoal(record._id);
              }}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
              placement="topRight"
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  const getGoalStats = () => {
    const total = filteredGoals.length;
    // Handle both new and legacy status values
    const active = filteredGoals.filter(g => g.status === 'Active' || g.status === 'In Progress').length;
    const completed = filteredGoals.filter(g => g.status === 'Completed').length;
    const notStarted = filteredGoals.filter(g => g.status === 'Not Started').length;
    const overdue = filteredGoals.filter(g => isOverdue(g)).length;

    // NEW: Review-related statistics
    const reviewOverdue = filteredGoals.filter(g => isReviewOverdue(g)).length;
    const needingReview = getGoalsNeedingReview(filteredGoals).length;

    // Use overallCompletionRate for enhanced SMART goals, fallback to legacy progress
    const avgProgress = total > 0 ?
      filteredGoals.reduce((sum, g) => {
        const progress = g.overallCompletionRate !== undefined ? g.overallCompletionRate : (g.progress?.percentage || 0);
        return sum + progress;
      }, 0) / total : 0;

    return { total, active, completed, notStarted, overdue, reviewOverdue, needingReview, avgProgress };
  };

  const stats = getGoalStats();

  return (
    <div className="smart-goals-container">
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                <AimOutlined /> SMART Goals
              </Title>
            </Col>
            <Col>
              <Space>
                <Radio.Group
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  size="small"
                >
                  <Radio.Button value="table">
                    <TableOutlined /> Table
                  </Radio.Button>
                  <Radio.Button value="cards">
                    <AppstoreOutlined /> Cards
                  </Radio.Button>
                  <Radio.Button value="tree">
                    <BranchesOutlined /> Tree
                  </Radio.Button>
                </Radio.Group>
                <Divider type="vertical" />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                  disabled={!problems.length}
                >
                  Create SMART Goal
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {!problems.length && (
          <Alert
            message="No Problems Available"
            description="SMART goals must be linked to a problem. Please add problems to the patient's problem list first."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Statistics Cards */}
        <Row gutter={16} className="smart-goals-stats-row">
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Goals"
                value={stats.total}
                prefix={<AimOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Active"
                value={stats.active}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Completed"
                value={stats.completed}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Not Started"
                value={stats.notStarted}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: '#d9d9d9' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Enhanced Filtering Controls */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} className="enhanced-smart-goals-filters">
          <Col span={12}>
            <Search
              placeholder="Search goals, problems, or MD orders..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="Not Started">Not Started</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Completed">Completed</Option>
              <Option value="On Hold">On Hold</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={systemFilter}
              onChange={setSystemFilter}
              style={{ width: '100%' }}
              placeholder="Filter by system"
            >
              <Option value="all">All Systems</Option>
              {Object.entries(ORGAN_SYSTEM_DISPLAY_NAMES).map(([key, value]) => (
                <Option key={key} value={key}>{value}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={problemFilter}
              onChange={setProblemFilter}
              style={{ width: '100%' }}
              placeholder="Filter by problem"
            >
              <Option value="all">All Problems</Option>
              {problems.map(problem => (
                <Option key={problem._id} value={problem._id}>
                  {problem.problemName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Button
              onClick={() => {
                setStatusFilter('all');
                setSystemFilter('all');
                setProblemFilter('all');
                setSearchText('');
              }}
              icon={<FilterOutlined />}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Review Reminders */}
        {(() => {
          const reminders = generateReviewReminders(filteredGoals);
          if (reminders.length === 0) return null;

          return (
            <Alert
              message={`${reminders.length} Goal${reminders.length > 1 ? 's' : ''} Need${reminders.length === 1 ? 's' : ''} Review Attention`}
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  {reminders.slice(0, 3).map(reminder => (
                    <div key={reminder.id}>
                      <Text strong style={{ color: reminder.type === 'error' ? '#f5222d' : '#fa8c16' }}>
                        {reminder.title}:
                      </Text>
                      <Text style={{ marginLeft: 8 }}>{reminder.message}</Text>
                    </div>
                  ))}
                  {reminders.length > 3 && (
                    <Text type="secondary">
                      ...and {reminders.length - 3} more goal{reminders.length - 3 > 1 ? 's' : ''}
                    </Text>
                  )}
                </Space>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              className="review-reminders-alert"
              action={
                <Button size="small" type="primary">
                  Review All
                </Button>
              }
            />
          );
        })()}

        <Spin spinning={loading}>
          {viewMode === 'table' && (
            <Table
              columns={columns}
              dataSource={filteredGoals}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} goals`,
                responsive: true
              }}
              size="small"
              scroll={{ x: 800 }}
              className="smart-goals-table"
              onRow={(record) => ({
                onClick: () => {
                  setSelectedGoal(record);
                  setDetailsModalVisible(true);
                },
                style: { cursor: 'pointer' }
              })}
            />
          )}

          {viewMode === 'cards' && (
            <div className="smart-goals-cards">
              {filteredGoals.map(goal => (
                <MultiGoalCard
                  key={goal._id}
                  goal={goal}
                  onEdit={(goal) => {
                    console.log('Opening edit modal for goal:', goal._id);
                    setSelectedGoal(goal);
                    setIsEditMode(true);
                    setDetailsModalVisible(true);
                  }}
                />
              ))}
              {filteredGoals.length === 0 && (
                <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                  <AimOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                  <div>No SMART goals match your current filters</div>
                </div>
              )}
            </div>
          )}

          {viewMode === 'tree' && (
            <PatientGoalTree
              goals={filteredGoals}
              onGoalSelect={(goalId, goal) => {
                console.log('Opening edit modal for goal from tree:', goal._id);
                setSelectedGoal(goal);
                setIsEditMode(true);
                setDetailsModalVisible(true);
              }}
              onPatientGoalSelect={(goalId, patientGoalId, patientGoal) => {
                setSelectedPatientGoalId(patientGoalId);
                // Find the parent goal and open details modal
                const parentGoal = filteredGoals.find(g => g._id === goalId);
                if (parentGoal) {
                  console.log('Opening edit modal for goal from tree patient goal:', parentGoal._id);
                  setSelectedGoal(parentGoal);
                  setIsEditMode(true);
                  setDetailsModalVisible(true);
                }
              }}
              selectedGoalId={selectedGoal?._id}
              selectedPatientGoalId={selectedPatientGoalId}
            />
          )}
        </Spin>
      </Card>

      {/* Create SMART Goal Modal - Enhanced */}
      <EnhancedSmartGoalModal
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          // Call parent's cancel handler to clear selectedProblem state
          if (onModalCancel) {
            onModalCancel();
          }
        }}
        onSuccess={handleCreateSuccess}
        patientId={patientId}
        problems={problems}
        selectedProblem={selectedProblem}
      />

      {/* Goal Details Modal - Enhanced */}
      {selectedGoal && (
        <EnhancedSmartGoalDetailsModal
          visible={detailsModalVisible}
          onCancel={() => {
            setDetailsModalVisible(false);
            setSelectedGoal(null);
            setIsEditMode(false);
          }}
          goal={selectedGoal}
          initialEditing={isEditMode}
          onUpdate={fetchSmartGoals}
          onDelete={async (goal) => {
            console.log('Delete initiated from details modal for goal:', goal._id);
            await handleDeleteGoal(goal._id);
            setDetailsModalVisible(false);
            setSelectedGoal(null);
            setIsEditMode(false);
          }}
        />
      )}
    </div>
  );
};

export default PatientSmartGoals;
